import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
  type OnChanges,
  type OnDestroy,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, type FormGroup } from '@angular/forms';
import { LetDirective } from '@ngrx/component';
import { PorscheDesignSystemModule, ToastManager } from '@porsche-design-system/components-angular';
import { BehaviorSubject, distinctUntilChanged, tap } from 'rxjs';
import { SubSink } from 'subsink';
import type { EditVendor, Vendor } from '../../../shared/models/api/vendor.model';
import type { ControlsOf } from '../../../shared/models/controls-of.model';
import type { NgChanges } from '../../../shared/models/ng-changes.model';
import { VendorService } from '../../../shared/services/api/vendor.service';
import { FormIdService } from '../../../shared/services/form-id.service';
import { OpaValidators } from '../../../shared/validators/opa.validators';
import type { EditVendorFormValue } from './vendor-edit.models';

@Component({
  selector: 'opa-vendor-edit',
  imports: [CommonModule, PorscheDesignSystemModule, ReactiveFormsModule, LetDirective],
  templateUrl: './vendor-edit.component.html',
  styleUrl: './vendor-edit.component.scss',
})
export class VendorEditComponent implements OnChanges, OnDestroy {
  private readonly formBuilder = inject(FormBuilder).nonNullable;
  private readonly vendorService = inject(VendorService);
  private readonly toastManager = inject(ToastManager);
  readonly formId = inject(FormIdService).getId();

  @Input({ required: true }) open = false;
  @Input({ required: true }) vendor: Vendor | undefined = undefined;
  @Output() dismiss = new EventEmitter<boolean | undefined>();

  private readonly subSink = new SubSink();
  readonly form = this.buildForm();
  private readonly submitting = new BehaviorSubject(false);
  readonly submitting$ = this.submitting.pipe(distinctUntilChanged());

  ngOnChanges(changes: NgChanges<VendorEditComponent>): void {
    if (changes.open && this.open) this.resetForm(this.vendor);
  }

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  close(success?: boolean): void {
    this.dismiss.emit(success);
  }

  submit(): void {
    if (!this.form.valid || !this.vendor) {
      this.form.markAllAsTouched();
      return;
    }

    this.submitting.next(true);
    const formValue = this.form.getRawValue();
    const vendor = this.formValueToVendor(formValue);
    this.subSink.sink = this.vendorService
      .editVendor(this.vendor.id, vendor)
      .pipe(
        tap({
          next: (editedVendor) => {
            this.toastManager.addMessage({
              text: `Vendor '${editedVendor.name}' edited`,
              state: 'success',
            });
            this.submitting.next(false);
            this.close(true);
          },
          error: () => this.submitting.next(false),
        }),
      )
      .subscribe();
  }

  private buildForm(): FormGroup<ControlsOf<EditVendorFormValue>> {
    const form = this.formBuilder.group<ControlsOf<EditVendorFormValue>>({
      name: this.formBuilder.control(null, OpaValidators.required()),
      legalName: this.formBuilder.control(null, OpaValidators.required()),
    });

    return form;
  }

  private formValueToVendor(form: EditVendorFormValue): EditVendor {
    const vendor: EditVendor = {
      vendorName: form.name!,
      legalName: form.legalName!,
    };
    return vendor;
  }

  private resetForm(vendor?: Vendor): void {
    this.form.reset();
    if (!vendor) return;

    this.form.patchValue({
      name: vendor.name,
      legalName: vendor.legalName,
    });
  }
}
